import { useMemo } from "react";
import { useScopedWhere } from "#app/widgets/dashboards/scope/use-scoped-where";
import { resolveWidgetFilters } from "#app/widgets/dashboards/widget-filters";
import { useIssueGroupCounts } from "#app/hooks/issue-list/use-issue-group-counts";
import { useWorkspaceData } from "#app/lib/data/workspace-data-provider";
import { combineWhereConditions } from "#app/lib/query-utils";

export function useProjectsClosed(includeLost: boolean, filters: unknown) {
  const { stages } = useWorkspaceData();
  const doneIds = useMemo(
    () =>
      stages
        .filter((s) => s.type === "done" || (includeLost && s.type === "canceled"))
        .map((s) => s.stageId),
    [stages, includeLost],
  );
  const { base, override } = resolveWidgetFilters(filters);
  const mergedBase = useMemo(
    () =>
      doneIds.length > 0
        ? combineWhereConditions([base, { stageId: { in: doneIds } }]) ?? {}
        : undefined,
    [base, doneIds],
  );
  const { where } = useScopedWhere(mergedBase, { override });
  const effectiveWhere = doneIds.length > 0 ? where : undefined;
  const { counts, ready, error } = useIssueGroupCounts({
    where: effectiveWhere,
    groupBy: doneIds.length > 0 ? "stageId" : undefined,
  });

  const value = useMemo(
    () => doneIds.reduce((sum, id) => sum + (counts[id] ?? 0), 0),
    [counts, doneIds],
  );

  if (doneIds.length === 0) return { value: 0, doneIds, loading: false, error };
  return { value, doneIds, loading: !ready, error };
}
